/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from '@material-ui/core';
import { Dropdown } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { viewPortHeight_355 } from '../../../../SharedModules/CommonConstants/commonConstants';
import TableGridWithFeatures from '../../../../SharedModules/Table/TableGridWithFeatures';
import CustomSeparator from '../../../../SharedModules/Breadcrumbs/BreadcrumbsComponent';
import Spinner from '../../../../SharedModules/Spinner/Spinner';
import ExportIcon from '../../../../assets/images/Icon-Component-Export.svg';
import { leftHead, rightHead } from './TableHead';
import { exportAllData } from './ExportAll';
import CommonModal from '../../../../SharedModules/Dialog/CommonModal';

const MonthlyClaimReconcilliationDetail = () => {
  const dispatch = useDispatch();

  const { isLoading, leftGridData, rightGridData, selectedRequest } =
    useSelector(
      (state) => state?.sharedState?.MonthlyClaimsReconcilliationReducer
    );
  const userDefaults = useSelector(
    (state) => state?.sharedState?.UserDefaultReducer?.UserDefaultsValues?.data
  );
  const logInUser = useSelector(
    (state) => state?.sharedState?.SharedReducer?.logInUser
  );

  const [filteredRows, setFilteredRow] = React.useState([]);
  const [leftTableRows, setLeftTableRows] = React.useState([]);
  const [rightTableRows, setRightTableRows] = React.useState([]);
  const [fullScreenView, setFullScreenView] = React.useState(false);
  const [filename, setDlgTxt] = React.useState('');
  const [dlgErr, setDlgErr] = React.useState(false);
  const [openDlg, setOpenDlg] = React.useState(false);

  // Function to toggle fullscreen view of table
  const changeFullScreenView = () => {
    setFullScreenView(!fullScreenView);
  };

  // Set the left table data rows
  React.useEffect(() => {
    setLeftTableRows(leftGridData);
  }, [leftGridData]);
  // Set the table data rows
  React.useEffect(() => {
    setRightTableRows(rightGridData);
  }, [rightGridData]);

  // To update the total row if not equals to any filter condition
  const [filterApply, setFilterApply] = React.useState(false);
  const getFilterApply = (filterApplied) => {
    setFilterApply(filterApplied);
  };

  /*
    Below threee functions are for calculating data of table bottom pinned row
  */

 const sum = (property, isAmount) => {
  const displayedRows = filterApply ? filteredRows : rightGridData;

  const data = displayedRows?.reduce((accumulator, row) => {
    if (row?._subTotalLabelCell || row?.isBottomRow) return accumulator;

    const value = row[property];
    return accumulator + (value ? Number(value) : 0);
  }, 0);

  if (isAmount) {
    return data
      ? data.toLocaleString('en-US', { currency: 'USD', style: 'currency' })
      : '$0.00';
  }
  return data ? data.toLocaleString('en-US') : '0';
};

  const createData = (count, prefix) => {
    const result = [];
    for (let i = 0; i < count; i++) {
      result.push({
        isBottomRow: true,
        head: true,
        reimbAmount: sum('reimbAmount', true),
        count: sum('count'),
        loadDate: 'Total:',
      });
    }
    return result;
  };
  const pinnedBottomRowData = useMemo(() => {
    return createData(1, 'Bottom');
  }, [rightTableRows, filteredRows, filterApply]);

  // To update the total row if not equals to any filter condition for the left grid
  const [filteredRowsLeft, setFilteredRowLeft] = React.useState([]);
  const [filterApplyLeft, setFilterApplyleft] = React.useState(false);
  const getFilterApplyleft = (filterApplied) => {
    setFilterApplyleft(filterApplied);
  };

  const sumLeft = (property, isAmount) => {
  const displayedRows = filterApplyLeft ? filteredRowsLeft : leftTableRows;

  const data = displayedRows?.reduce((accumulator, row) => {
    if (row?._subTotalLabelCell || row?.isBottomRow) return accumulator;

    const value = row[property];
    return accumulator + (value ? Number(value) : 0);
  }, 0);

  return data ? data.toLocaleString('en-US') : '0';
};

  const createDataLeft = (count, prefix) => {
    const displayedRows = filterApplyLeft ? filteredRowsLeft : leftTableRows;
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const months = new Set();
    (displayedRows || []).forEach((r) => {
      const m = r?.claimDtMonth;
      if (m !== null && m !== undefined && m !== '') {
        const mm = String(m).padStart(2, '0');
        months.add(mm);
      }
    });
    let label = 'Total:';
    if (months.size === 1) {
      const mm = [...months][0];
      const idx = Number(mm) - 1;
      if (!Number.isNaN(idx) && idx >= 0 && idx < 12) {
        label = `Total`;
      }
    } else if (months.size > 1) {
      label = 'Total';
    }

    const result = [];
    for (let i = 0; i < count; i++) {
      result.push({
        isBottomRow: true,
        head: false,
        recordCount: sumLeft('recordCount'),
        claimLoadDate: label,
      });
    }
    return result;
  };
  const pinnedBottomRowDataLeft = useMemo(() => {
    return createDataLeft(1, 'Bottom');
  }, [leftTableRows, filteredRowsLeft, filterApplyLeft]);

  const onFilterChangedLeft = (rowData, type) => {
    setFilteredRowLeft(rowData);
  };

  // Right
  const onFilterChangedRight = (rowData, type) => {
    const updatedRowData = rowData.map((item) => {
      return {
        ...item,
        count: parseFloat(item.count.replace('$', '').replace(/,/g, '')),
        reimbAmount: parseFloat(
          item.reimbAmount.replace('$', '').replace(/,/g, '')
        ),
      };
    });
    setFilteredRow(updatedRowData);
  };

  // Below 2 variable for PDF header slected information

  const secondInfo = `Submitted By: ${selectedRequest?.requestorUserId || ''}`;
  const fourthInfo = `Date Range: ${selectedRequest.startDate || ''} - ${
    selectedRequest.endDate || ''
  }`;
  const groupRowsWithSubtotals = (
    rows = [],
    dateField = 'claimLoadDate',
    countField = 'recordCount',
    rebateProgramField = 'rebateProgramCd',
    formatCounts = true
  ) => {
    if (!rows || rows.length === 0) return []; 
    const monthGroups = {};
    rows.forEach((row) => {
      const month = row?.claimDtMonth;
      if (!monthGroups[month]) {
        monthGroups[month] = [];
      }
      monthGroups[month].push(row);
    });

    const monthNames = [
      'JAN:',
      'FEB:',
      'MAR:',
      'APR:',
      'MAY:',
      'JUN:',
      'JUL:',
      'AUG:',
      'SEP:',
      'OCT:',
      'NOV:',
      'DEC:',
    ];

    const result = []; 

    Object.keys(monthGroups)
      .sort()
      .forEach((month) => {
        const monthRows = monthGroups[month];
        const orderedDates = [...new Set(monthRows.map((r) => r?.[dateField]))];
        let monthTotal = 0; 

        orderedDates.forEach((date) => {
          const group = monthRows.filter((r) => r?.[dateField] === date);
          group.forEach((item) => {
            const numericCount = Number(item[countField]) || 0;
            result.push({ ...item, [countField]: numericCount });
          });

          const subtotal = group.reduce(
            (acc, it) => acc + (Number(it[countField]) || 0),
            0
          );
          monthTotal += subtotal;

          result.push({
            isBottomRow: true,
            head: true,
            [rebateProgramField]: `Subtotal:`,
            [countField]: subtotal,
            _subTotalLabelCell: true,
          });
        }); 

        const monthIdx = Number(month) - 1;
        const monthLabel =
          !Number.isNaN(monthIdx) && monthIdx >= 0 && monthIdx < 12
            ? monthNames[monthIdx]
            : `Month ${month}`;

        result.push({
          isBottomRow: true,
          head: true,
          [rebateProgramField]: `Total for ${monthLabel}`,
          [countField]: monthTotal,
          _subTotalLabelCell: true,
        });
      });
    if (!formatCounts) return result;
    return result.map((item) => ({
      ...item,
      [countField]:
        item[countField] != null
          ? Number(item[countField]).toLocaleString('en-US')
          : item[countField],
    }));
  };
  const exportAll = () => {
    exportAllData({
      leftGridData: groupRowsWithSubtotals(
        leftTableRows,
        'claimLoadDate',
        'recordCount',
        'rebateProgramCd',
        false
      ),
      rightGridData,
      userDefaults,
      selectedRequest,
      logInUser,
      filename,
    });
    setDlgTxt('');
  };
  return (
    <div className="tabs-container">
      <Spinner load={isLoading} />
      <CustomSeparator
        parent1="State Reports"
        parent2="Monthly Claims Reconciliation Details"
      />

      <div className="tab-body">
        {!fullScreenView && (
          <>
            <div className="row">
              <div className="col-12  ">
                <div className="form-wrapper p-0 mt-2">
                  <div className="mui-custom-form input-md field-xl">
                    <div className="row mb-2">
                      <div className="col-6">
                        <TextField
                          label="Date Range: "
                          id="daterange"
                          fullWidth
                          placeholder=""
                          inputProps={{ id: 'daterange' }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled
                          value={`${selectedRequest.startDate} - ${selectedRequest.endDate}`}
                        />
                      </div>
                      <div className="col-6">
                        <TextField
                          label="Submitted By: "
                          id="submitby"
                          fullWidth
                          placeholder=""
                          InputLabelProps={{
                            shrink: true,
                          }}
                          disabled
                          value={selectedRequest?.requestorUserId}
                        />
                      </div>
                    </div>
                    <div className="float-right">
                      <div className="col-6">
                        <>
                          <Dropdown className="icon-dropdown-container">
                            <Dropdown.Toggle
                              className="icon-dropdown"
                              aria-label="Actions Button"
                            >
                              <OverlayTrigger
                                placement="bottom"
                                overlay={
                                  <Tooltip id="action-tooltip">Actions</Tooltip>
                                }
                              >
                                <img
                                  alt="Export Button"
                                  style={{ marginRight: -55, width: 32 }}
                                  src={ExportIcon}
                                />
                              </OverlayTrigger>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item
                                data-testid="export-all"
                                onClick={() => setOpenDlg(true)}
                              >
                                Export to PDF
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                          <CommonModal
                            open={openDlg}
                            className="dialog-420"
                            viewAction
                            viewHeader
                            onOk={() => {
                              setOpenDlg(false);
                              exportAll();
                            }}
                            okTitle="Ok"
                            disableOk={!filename || dlgErr}
                            onClose={() => {
                              setOpenDlg(false);
                              setDlgTxt('');
                            }}
                            closeTitle="Cancel"
                            modalTitle="Save Data In File"
                            autoFocus
                            childrenComponent={
                              <div className="form-wrapper">
                                <div className="mui-custom-form">
                                  <TextField
                                    className="mb-3"
                                    id="filename"
                                    inputProps={{ id: 'filename' }}
                                    label="File Name"
                                    required
                                    autoFocus
                                    InputLabelProps={{ shrink: true }}
                                    data-testid="filename-input"
                                    value={filename}
                                    onChange={(e) => {
                                      setDlgTxt(e.target.value);
                                      if (
                                        e.target.value &&
                                        !/^[\w\-. ]+$/.test(e.target.value)
                                      )
                                        setDlgErr(true);
                                      else setDlgErr(false);
                                    }}
                                    error={dlgErr}
                                    helperText={
                                      dlgErr
                                        ? 'Invalid character in file name'
                                        : ''
                                    }
                                  />
                                </div>
                              </div>
                            }
                          />
                        </>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <div className="row" style={{ marginTop: 15 }}>
          <div className="unallocated col-6" style={{ width: '100vw' }}>
            <TableGridWithFeatures
              gridHeight={fullScreenView ? '70vh' : viewPortHeight_355}
              columnDefs={leftHead}
              rowData={groupRowsWithSubtotals(leftTableRows, 'claimLoadDate', 'recordCount','rebateProgramCd')}
              data-testid="onEditingStopped-left"
              enterMovesDown
              showResetFilter={false}
              defaultCol={{
                wrapHeaderText: true,
                autoHeaderHeight: true,
              }}
              hideTotalCount={true}
              exportFile
              fileName="MonthlyClaimsRecon"
              reportName="Monthly Claims Reconciliation Report"
              fullScreenView={fullScreenView}
              handleFullScreenViewClick={changeFullScreenView}
              info={{ secondInfo, fourthInfo }}
              pinnedBottomRowData={pinnedBottomRowDataLeft}
              onFilterData={onFilterChangedLeft}
              getFilterApply={getFilterApplyleft}
            />
          <p className="total-results" tabIndex="0">
          Total Results: <b>{(filterApplyLeft ? filteredRowsLeft : leftTableRows)?.length?.toLocaleString('en-US')}</b>
          </p>
          </div>
          <div className="unallocated col-6" style={{ width: '100vw' }}>
            <TableGridWithFeatures
              gridHeight={fullScreenView ? '70vh' : viewPortHeight_355}
              columnDefs={rightHead}
              rowData={rightTableRows?.map((item) => {
                return {
                  ...item,
                  count: item.count?.toLocaleString('en-US'),
                  reimbAmount: item.reimbAmount?.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }),
                };
              })}
              onFilterData={onFilterChangedRight}
              data-testid="onEditingStopped"
              enterMovesDown
              showResetFilter={false}
              defaultCol={{
                wrapHeaderText: true,
                autoHeaderHeight: true,
              }}
              hideTotalCount={false}
              exportFile
              fileName="MonthlyClaimsRecon"
              reportName="Monthly Claims Reconciliation Report"
              fullScreenView={fullScreenView}
              handleFullScreenViewClick={changeFullScreenView}
              info={{ secondInfo, fourthInfo }}
              pinnedBottomRowData={pinnedBottomRowData}
              getFilterApply={getFilterApply}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyClaimReconcilliationDetail;
